/**
 * @TODO Since all of these functions are being used across the project,
 * more reasonable file name is needed. Alternatively, split into smaller atomic
 * files, I guess we should elaborate on convention for files containing utility
 * functions, i.e. one function per file under /lib/xxx/utils/
 * or just always name it /lib/xxx/utils.ts
 */

import {
  Control,
  ControlConditionSingle,
  ControlsTreeContext
} from "@/types/controls";
import { separatePathPrefixFromElementControlPath } from "../elements/utils";
import { configStore, uiStore } from "@/store/store";
export function compare(aValue: unknown, operand?: string, bValue?: unknown) {
  if (operand === "equals") {
    return aValue == bValue; // TODO - strict equality?
  } else if (operand === "not equals") {
    return aValue != bValue;
  } else if (operand === "is one of") {
    if (Array.isArray(bValue)) {
      return bValue.some(x => x == aValue);
    }
  } else if (operand === "is none of") {
    if (Array.isArray(bValue)) {
      return !bValue.some(x => x == aValue);
    }
  } else if (operand === "is set") {
    return !!aValue;
  } else if (operand === "is not set") {
    return !aValue;
  } else if (operand === "includes") {
    if (Array.isArray(aValue) && Array.isArray(bValue)) {
      // is any value in aValue contained in bValue?
      return aValue.some(x => bValue.includes(x));
    }

    if (Array.isArray(aValue)) {
      return aValue.includes(bValue);
    }

    return false;
  } else if (operand === "contains") {
    if (typeof aValue === "string" && typeof bValue === "string") {
      return aValue.includes(bValue);
    }
    return false;
  } else if (operand === "does not contain") {
    if (typeof aValue === "string" && typeof bValue === "string") {
      return !aValue.includes(bValue);
    }
    return true;
  }

  return true;
}

function areConditionsTrue(
  conditions: ControlConditionSingle[],
  path: string,
  context: ControlsTreeContext,
  currentBreakpointId: string
) {
  return (
    conditions
      .map(condition =>
        compare(
          getControlValueWithParsedPath(
            condition.path,
            path,
            context,
            currentBreakpointId
          ),
          condition.operand,
          condition.value
        )
      )
      // All conditions must be true to pass (AND)
      .filter(Boolean).length === conditions.length
  );
}

export function shouldShowControl(
  control: Control,
  path: string,
  context: ControlsTreeContext,
  currentBreakpointId: string
) {
  if (
    control.options.type === "pro_only_alert_box" &&
    configStore.subscriptionMode !== "free"
  )
    return false;

  if (control.options.hideForElements && uiStore.activeElement) {
    const shouldHideControlForCurrentElement =
      control.options.hideForElements.includes(uiStore.activeElement.data.type);

    if (shouldHideControlForCurrentElement) {
      return false;
    }
  }

  if (!control.options.condition) {
    return true;
  }

  if (Array.isArray(control.options.condition)) {
    if (!control.options.condition.length) return true;

    return (
      control.options.condition
        .map(conditions =>
          areConditionsTrue(conditions, path, context, currentBreakpointId)
        )
        // Any condition passes? (OR)
        .filter(Boolean).length
    );
  } else {
    // for backwards compatibility
    return compare(
      getControlValueWithParsedPath(
        control.options.condition.path,
        path,
        context,
        currentBreakpointId
      ),
      control.options.condition.operand,
      control.options.condition.value
    );
  }
}

function getControlValueWithParsedPath(
  conditionPath: string,
  path: string,
  context: ControlsTreeContext,
  currentBreakpointId: string
) {
  // Remove the last control in the path
  const currentPath = path.split(".");
  currentPath.pop();
  const joinedCurrentPath = currentPath.join(".");
  currentPath.pop();
  const joinedParentPath = currentPath.join(".");

  // 'replace' returns original string if not found
  const parsedPath = conditionPath
    .replace("%%CURRENTPATH%%", joinedCurrentPath)
    .replace("%%PARENTPATH%%", joinedParentPath);

  const contextControls = context.getControls(path);

  if (!contextControls) {
    return false;
  }

  const splittedPath = separatePathPrefixFromElementControlPath(parsedPath);

  const pathWithoutPathPrefix =
    splittedPath?.pathWithoutPathPrefix ?? parsedPath;

  const fullPath = getControlAtPath(pathWithoutPathPrefix, contextControls)
    ?.enableMediaQueries
    ? `${parsedPath}.${currentBreakpointId}`
    : parsedPath;

  return context.getControlValue(fullPath);
}

export function getControlAtPath(
  controlPath: string,
  contextControls: Control[]
) {
  const pathParts = controlPath.split(".");

  let controls = contextControls;
  let control: Control | undefined;

  for (const pathPart of pathParts) {
    let path = pathPart;

    if (path.charAt(path.length - 1) === "]") {
      // Replace brackets with numbers inside for nothing
      // Needed to get the control if the Repeater contains a Control (aka an array of Control[])
      path = path.replace(/\[[0-9]+]/, "");
    }

    // We're 'descending' through the controls and children until the last control of the path
    // E.G: 'designSection.background.type'. We'll visit 'background', then 'type'
    control = controls.find(control => control.slug === path);

    if (control?.children?.length) {
      controls = control.children;
    } else {
      break;
    }
  }

  return control;
}
